import React, { useEffect } from 'react';

const SMSConsent = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div style={{
      maxWidth: '800px',
      margin: '0 auto',
      padding: '40px 20px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      lineHeight: '1.6',
      color: '#1f2937'
    }}>
      <h1 style={{ fontSize: '2.5rem', marginBottom: '8px', color: '#111827' }}>
        SMS Consent & Terms of Service
      </h1>
      <p style={{ color: '#6b7280', marginBottom: '32px' }}>Last Updated: November 5, 2025</p>

      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '12px', color: '#374151' }}>
          How SMS Messaging Works
        </h2>
        <p>
          VinCentrix is a technology platform that enables automotive dealerships to communicate 
          with their customers via SMS text messages regarding trade-in offers and vehicle valuations.
        </p>
      </section>

      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '12px', color: '#374151' }}>
          Consent to Receive Messages
        </h2>
        <p>When you provide your mobile phone number to a dealership that uses VinCentrix services, 
        you consent to receive text messages from that dealership regarding:</p>
        <ul style={{ marginLeft: '20px', marginTop: '12px' }}>
          <li>Personalized trade-in offers for your vehicle</li>
          <li>Vehicle valuation information</li>
          <li>Follow-up messages about your trade-in inquiry</li>
          <li>Service-related communications</li>
        </ul>
      </section>

      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '12px', color: '#374151' }}>
          How Consent is Obtained
        </h2>
        <p>Consent is obtained through one or more of the following methods:</p>
        <ul style={{ marginLeft: '20px', marginTop: '12px' }}>
          <li><strong>Service Visit:</strong> When you visit a dealership for vehicle service and 
          provide your phone number, you may receive trade-in offer communications</li>
          <li><strong>Verbal Agreement:</strong> Service advisors may inform you that you'll receive 
          a text message with a trade-in offer and confirm your agreement</li>
          <li><strong>Written Consent:</strong> Service intake forms may include checkbox consent 
          for SMS communications</li>
          <li><strong>Website Forms:</strong> Submitting contact information through a dealership's 
          website with SMS consent checkbox</li>
        </ul>
      </section>

      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '12px', color: '#374151' }}>
          Message Frequency
        </h2>
        <p>
          Message frequency varies. You may receive an initial trade-in offer message, followed by 
          up to 3 follow-up messages if you show interest. Typical frequency: 1-4 messages over a 4-day period.
        </p>
      </section>

      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '12px', color: '#374151' }}>
          Message and Data Rates
        </h2>
        <p>
          Message and data rates may apply based on your mobile carrier's plan. Standard SMS rates apply. 
          VinCentrix does not charge for messages, but your carrier may.
        </p>
      </section>

      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '12px', color: '#374151' }}>
          Supported Carriers
        </h2>
        <p>
          Compatible with all major U.S. carriers including AT&T, Verizon, T-Mobile, Sprint, 
          and other regional carriers.
        </p>
      </section>

      <section style={{ marginBottom: '32px', background: '#fef3c7', padding: '20px', borderRadius: '8px', border: '1px solid #fbbf24' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '12px', color: '#92400e' }}>
          How to Opt-Out
        </h2>
        <p>You can opt out of SMS messages at any time by:</p>
        <ul style={{ marginLeft: '20px', marginTop: '12px' }}>
          <li>Replying <strong>STOP</strong>, <strong>UNSUBSCRIBE</strong>, <strong>CANCEL</strong>, 
          <strong>END</strong>, or <strong>QUIT</strong> to any message</li>
          <li>You will receive a confirmation message that you've been removed</li>
          <li>Contacting the dealership directly and requesting removal</li>
        </ul>
        <p style={{ marginTop: '12px' }}>
          After opting out, you will not receive any further SMS messages from that dealership 
          through VinCentrix unless you re-consent.
        </p>
      </section>

      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '12px', color: '#374151' }}>
          Help & Customer Support
        </h2>
        <p>For help, reply <strong>HELP</strong> to any message, or contact:</p>
        <ul style={{ marginLeft: '20px', marginTop: '12px' }}>
          <li>Email: <a href="mailto:support@vincentrix.com" style={{ color: '#3b82f6' }}>support@vincentrix.com</a></li>
          <li>The dealership that sent you the message directly</li>
        </ul>
      </section>

      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '12px', color: '#374151' }}>
          Privacy & Data Usage
        </h2>
        <p>
          Your phone number and personal information are used solely to provide trade-in offers 
          and related communications. We do not sell your personal information to third parties. 
          See our <a href="/privacy-policy" style={{ color: '#3b82f6' }}>Privacy Policy</a> for more details.
        </p>
      </section>

      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '12px', color: '#374151' }}>
          TCPA Compliance
        </h2>
        <p>
          VinCentrix complies with the Telephone Consumer Protection Act (TCPA) and all applicable 
          SMS messaging regulations. Messages are sent only to individuals with whom dealerships have 
          an established business relationship and proper consent.
        </p>
      </section>

      <section style={{ marginBottom: '32px' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '12px', color: '#374151' }}>
          Changes to This Policy
        </h2>
        <p>
          We may update this SMS Consent Policy from time to time. The "Last Updated" date at the 
          top indicates when changes were made.
        </p>
      </section>

      <section style={{ marginBottom: '32px', background: '#f3f4f6', padding: '20px', borderRadius: '8px' }}>
        <h2 style={{ fontSize: '1.5rem', marginBottom: '12px', color: '#374151' }}>
          Contact Us
        </h2>
        <p>Questions about SMS messaging?</p>
        <p style={{ marginTop: '8px' }}>
          Email: <a href="mailto:david@vincentrix.com" style={{ color: '#3b82f6' }}>david@vincentrix.com</a><br />
          Company: VinCentrix, LLC
        </p>
      </section>

    </div>
  );
};

export default SMSConsent;

